import React, { type ReactNode, useEffect, useReducer, useRef } from 'react';

import __noop from '@atlaskit/ds-lib/noop';
import { setBooleanFeatureFlagResolver } from '@atlaskit/platform-feature-flags/setBooleanFeatureFlagResolver';

import { defaultFeatureFlags } from './default-feature-flags';
import { FeatureFlagContext } from './feature-flag-context';
import { type FeatureFlagList } from './types';
import { useFeatureFlagOverrides } from './use-feature-flag-overrides';
import { useFeatureFlags } from './use-feature-flags';

type Action = { type: 'SET_FEATURE_FLAG'; payload: { featureFlagName: string; value: boolean } };

const featureFlagReducer = (state: FeatureFlagList, action: Action): FeatureFlagList => {
	switch (action.type) {
		case 'SET_FEATURE_FLAG':
			const { featureFlagName, value } = action.payload;
			return {
				...state,
				[featureFlagName]: value,
			};
		default:
			return state;
	}
};

type FeatureFlagProviderProps = {
	children?: ReactNode;
	/**
	 * Initial feature flags and values to use.
	 * If not provided, the default feature flags will be used.
	 */
	initialFeatureFlags?: FeatureFlagList;
};

/**
 * __Feature flag provider__
 *
 * Provides feature flag configuration to components within.
 * Allows feature flags to be updated, or overridden for limited subtrees
 * in combination with a `FeatureFlagOverride`.
 */
export const FeatureFlagProvider: ({
	children,
	initialFeatureFlags,
}: FeatureFlagProviderProps) => React.JSX.Element = ({
	children,
	initialFeatureFlags,
}: FeatureFlagProviderProps): React.JSX.Element => {
	const [features, dispatch] = useReducer(
		featureFlagReducer,
		initialFeatureFlags ?? defaultFeatureFlags,
	);

	// Store latest feature flags for non-hook fallback
	latestFeatureFlags = features;

	const isResolverSet = useRef(false);

	if (isResolverSet.current === false) {
		setBooleanFeatureFlagResolver(useResolver);
		isResolverSet.current = true;
	}

	useEffect(() => {
		return () => {
			setBooleanFeatureFlagResolver(() => false);
		};
	}, []);

	const setFeatureFlag = (featureFlagName: string, value: boolean) => {
		dispatch({ type: 'SET_FEATURE_FLAG', payload: { featureFlagName, value } });
	};

	return (
		<FeatureFlagContext.Provider
			value={{
				features,
				setFeatureFlag,
			}}
		>
			{children}
		</FeatureFlagContext.Provider>
	);
};

/**
 * Returns the value of a feature flag, including any overrides set by \<FeatureFlagOverrides\>.
 * Throws an exception if called outside a React function component or in a scope (such as useEffect) where React hooks cannot be used.
 *
 * @param featureFlagName - The name of the feature flag to check.
 * @returns The value of the feature flag, including overrides.
 */
const useFeatureFlagResolverWithOverrides = (featureFlagName: string) => {
	const key = featureFlagName as keyof FeatureFlagList;
	const { features } = useFeatureFlags();
	const overrides = useFeatureFlagOverrides();

	if (Object.keys(overrides).length > 0 && overrides[key] !== undefined) {
		return overrides[key] || false;
	}

	return features[key] || false;
};

/**
 * Returns the value of a feature flag as set by \<FeatureFlagProvider\>.
 * Can be used anywhere, including outside React components. Does not include any overrides from \<FeatureFlagOverrides\>.
 *
 * @param featureFlagName - The name of the feature flag to check.
 * @returns The value of the feature flag as set by the provider.
 */
const resolveFeatureFlagWithoutOverrides = (featureFlagName: string) => {
	const key = featureFlagName as keyof FeatureFlagList;
	if (latestFeatureFlags && latestFeatureFlags[key] !== undefined) {
		return latestFeatureFlags[key];
	}
	return defaultFeatureFlags[key] || false;
};

/**
 * Retrieves the value of a feature flag, using React hooks to include any overrides from \<FeatureFlagOverrides\> when possible.
 * If called outside a React function component or in a scope where hooks cannot be used (such as useEffect or non-React code),
 * automatically falls back to a static resolver that returns the default or most recently known value, without any overrides.
 * This ensures a value is always returned, but override values are only available within React components.
 *
 * @param featureFlagName - The name of the feature flag to check.
 * @returns The value of the feature flag, including overrides if available, otherwise the default or last known value.
 */
export const useResolver: (featureFlagName: string) => boolean = (
	featureFlagName: string,
): boolean => {
	try {
		return useFeatureFlagResolverWithOverrides(featureFlagName);
	} catch {
		return resolveFeatureFlagWithoutOverrides(featureFlagName);
	}
};

// Module-level variable to store latest feature flags for fallback
let latestFeatureFlags: FeatureFlagList = defaultFeatureFlags;
